import { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Check, Copy, Loader2, Sparkles } from "lucide-react";
import { SegmentedToggle } from "@/components/pages/calendar/facet-filter";

const EYEBROW = "text-[11px] font-bold uppercase tracking-wide text-muted-foreground";

// This switch changes how the draft is treated (hint vs. text to polish) rather than adding
// a line to the reply, so it gets its own row with a visible description instead of sitting
// in the "Include in the reply" group with the explanation hidden in a tooltip.
const MODE_SWITCH_ID = "expandFromContext";

/**
 * A labelled checkbox rendered as a togglable pill (the app has no Checkbox primitive).
 * @param {{ checked: boolean, disabled?: boolean, label: string, title?: string, onChange: (v: boolean) => void }} props
 */
function CheckboxPill({ checked, disabled, label, title, onChange }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      title={title}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        "inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors",
        checked
          ? "border-primary/25 bg-primary/10 text-foreground"
          : "border-border bg-transparent text-muted-foreground hover:bg-muted/50 hover:text-foreground",
        disabled && "cursor-not-allowed opacity-60",
      )}
    >
      <span
        className={cn(
          "flex size-4 items-center justify-center rounded-[5px] border transition-colors",
          checked ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/40",
        )}
      >
        {checked && <Check className="size-3" strokeWidth={3.5} aria-hidden />}
      </span>
      {label}
    </button>
  );
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {string} props.sourceText
 * @param {(v: string) => void} props.onSourceChange
 * @param {string} props.outputText
 * @param {(v: string) => void} props.onOutputChange
 * @param {boolean} props.generating
 * @param {() => void | Promise<void>} props.onGenerate
 * @param {() => void} props.onApply
 * @param {import("@/lib/api/admin").DraftReplyPersona[]} props.personas
 * @param {import("@/lib/api/admin").DraftReplySwitchDef[]} props.switchDefs
 * @param {string} props.persona Selected persona id.
 * @param {(id: string) => void} props.onPersonaChange
 * @param {Record<string, boolean>} props.options Resolved switch values (persona defaults + user overrides).
 * @param {(id: string, value: boolean) => void} props.onOptionChange
 */
export function DraftReplyAiDialog({
  open,
  onOpenChange,
  sourceText,
  onSourceChange,
  outputText,
  onOutputChange,
  generating,
  onGenerate,
  onApply,
  personas,
  switchDefs,
  persona,
  onPersonaChange,
  options,
  onOptionChange,
}) {
  const [copied, setCopied] = useState(false);

  // Split out the mode switch; everything else (including ids this build doesn't know
  // about) keeps rendering in the pill group, and an older backend that omits the mode
  // switch simply renders no extra row.
  const modeDef = switchDefs.find((def) => def.id === MODE_SWITCH_ID);
  const includeDefs = switchDefs.filter((def) => def.id !== MODE_SWITCH_ID);

  const hasOutput = outputText.trim().length > 0;
  const canGenerate = sourceText.trim().length > 0 && !generating;
  const canApply = hasOutput && !generating;
  const wordCount = hasOutput ? outputText.trim().split(/\s+/).length : 0;

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && canGenerate) {
      e.preventDefault();
      void onGenerate();
    }
  };

  const handleCopy = async () => {
    if (!hasOutput) return;
    try {
      await navigator.clipboard.writeText(outputText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard can fail (permissions/focus); silently ignore.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onKeyDown={handleKeyDown}
        className="flex max-h-[88vh] w-full max-w-5xl flex-col gap-0 overflow-hidden border-border/80 p-0 shadow-lg sm:max-w-5xl sm:rounded-2xl"
      >
        <DialogHeader className="shrink-0 space-y-0 border-b border-border/60 px-6 pb-5 pt-6 text-left">
          <div className="flex items-start gap-3.5">
            <div className="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-primary/15 text-primary ring-1 ring-primary/20 dark:bg-primary/10 dark:ring-primary/25">
              <Sparkles className="size-5" strokeWidth={2.25} aria-hidden />
            </div>
            <div className="space-y-1 pr-6">
              <DialogTitle className="text-xl font-semibold tracking-tight">Rewrite with AI</DialogTitle>
              <DialogDescription className="text-[13px] leading-relaxed text-muted-foreground">
                Turn rough notes into a polished, patient-ready reply. Nothing is sent until you press Send.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex shrink-0 items-center gap-4 border-b border-border/60 px-6 py-3.5">
          <span className={cn(EYEBROW, "shrink-0")}>Tone</span>
          <div className={cn("min-w-0 flex-1", generating && "pointer-events-none opacity-50")}>
            <SegmentedToggle
              value={persona}
              onChange={(id) => !generating && onPersonaChange(id)}
              ariaLabel="Reply tone"
              size="md"
              stretch
              segments={personas.map((p) => ({ id: p.id, label: p.label, title: p.description }))}
            />
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          {/* Left: inputs + options + generate */}
          <ScrollArea className="shrink-0 border-b border-border/60 md:w-[42%] md:border-b-0 md:border-r">
            <div className="flex flex-col gap-5 p-6">
            <div className="space-y-2">
              <label htmlFor="draft-reply-ai-source" className={EYEBROW}>
                Your draft or notes
              </label>
              <Textarea
                id="draft-reply-ai-source"
                value={sourceText}
                onChange={(e) => onSourceChange(e.target.value)}
                placeholder="e.g. appt moved to Fri 10:00, bring previous test results"
                disabled={generating}
                rows={4}
                className="resize-y leading-relaxed"
              />
            </div>

            {modeDef && (
              <div className="space-y-2">
                <span className={EYEBROW}>Depth</span>
                <div className="space-y-1.5">
                  <CheckboxPill
                    checked={Boolean(options[modeDef.id])}
                    disabled={generating}
                    label={modeDef.label}
                    onChange={(v) => onOptionChange(modeDef.id, v)}
                  />
                  <p className="text-[12px] leading-relaxed text-muted-foreground">{modeDef.description}</p>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <span className={EYEBROW}>Include in the reply</span>
              <div className="flex flex-wrap gap-2">
                {includeDefs.map((def) => (
                  <CheckboxPill
                    key={def.id}
                    checked={Boolean(options[def.id])}
                    disabled={generating}
                    label={def.label}
                    title={def.description}
                    onChange={(v) => onOptionChange(def.id, v)}
                  />
                ))}
              </div>
            </div>

            <Button
              type="button"
              className="w-full gap-2"
              disabled={!canGenerate}
              onClick={() => void onGenerate()}
            >
              {generating ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <Sparkles className="size-4" aria-hidden />
              )}
              {generating ? "Generating…" : hasOutput ? "Regenerate" : "Generate suggestion"}
            </Button>
            </div>
          </ScrollArea>

          {/* Right: suggested reply */}
          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 p-6">
            <div className="flex shrink-0 items-center justify-between gap-3">
              <span className={EYEBROW}>Suggested reply</span>
              <button
                type="button"
                onClick={handleCopy}
                disabled={!hasOutput}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
              >
                <Copy className="size-3.5" aria-hidden />
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <Textarea
              id="draft-reply-ai-output"
              value={outputText}
              onChange={(e) => onOutputChange(e.target.value)}
              placeholder="Your polished reply will appear here. You can edit it before applying."
              disabled={generating}
              className="min-h-0 flex-1 resize-none leading-relaxed"
            />
            <div className="flex shrink-0 items-center justify-between gap-3 text-[11px] text-muted-foreground">
              <span>Review before sending — AI can miss details.</span>
              {hasOutput && (
                <span className="shrink-0">
                  {wordCount} {wordCount === 1 ? "word" : "words"}
                </span>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="shrink-0 flex-row items-center justify-between gap-3 border-t border-border/60 px-6 py-4 sm:justify-between">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <div className="flex items-center gap-4">
            <span className="hidden text-[11px] text-muted-foreground sm:inline">Ctrl / ⌘ + ↵ to generate</span>
            <Button type="button" className="min-w-32 gap-1.5" onClick={onApply} disabled={!canApply}>
              <Check className="size-4" aria-hidden />
              Use in message
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
